import { useEffect, useState, type MouseEvent } from "react";
import { ExternalLink, FileText, Loader2, X } from "lucide-react";
import { errMsg } from "@shared/api/errors";
import { isoCertificatesApi } from "../api/certificados-iso";
import type { IsoCertificateSlot } from "../types/certificados-iso";
import "../styles/certificados-iso.css";

type Props = {
  slot: IsoCertificateSlot | null;
  onClose: () => void;
};

export function IsoCertificatePreviewModal({ slot, onClose }: Props) {
  const [url, setUrl] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (!slot?.available) {
      setUrl(null);
      setError(null);
      return;
    }

    let active = true;
    let objectUrl: string | null = null;
    setUrl(null);
    setError(null);

    isoCertificatesApi.preview(slot.codigo)
      .then((blob) => {
        if (!active) {
          return;
        }

        objectUrl = URL.createObjectURL(blob);
        setUrl(objectUrl);
      })
      .catch((requestError: unknown) => {
        if (active) {
          setError(errMsg(requestError, "No se pudo cargar la vista previa del certificado."));
        }
      });

    return () => {
      active = false;
      if (objectUrl) {
        URL.revokeObjectURL(objectUrl);
      }
    };
  }, [slot]);

  useEffect(() => {
    if (!slot) {
      return;
    }

    const handleKeyDown = (event: KeyboardEvent) => {
      if (event.key === "Escape") {
        onClose();
      }
    };

    window.addEventListener("keydown", handleKeyDown);
    return () => window.removeEventListener("keydown", handleKeyDown);
  }, [onClose, slot]);

  if (!slot) {
    return null;
  }

  return (
    <div className="iso-certificates-modal-backdrop" role="presentation" onMouseDown={onClose}>
      <div
        className="iso-certificates-modal iso-certificates-modal-wide"
        role="dialog"
        aria-modal="true"
        aria-labelledby="iso-certificate-preview-title"
        onMouseDown={(event: MouseEvent<HTMLDivElement>) => event.stopPropagation()}
      >
        <header className="iso-certificates-modal-header">
          <div className="iso-certificates-modal-copy">
            <span>Certificado oficial</span>
            <h2 id="iso-certificate-preview-title">{slot.label}</h2>
            {slot.documento ? <p>{slot.documento.nombre_original}</p> : null}
          </div>
          <button
            type="button"
            className="iso-certificates-modal-icon"
            aria-label="Cerrar"
            onClick={onClose}
          >
            <X size={18} />
          </button>
        </header>

        <div className="iso-certificates-modal-body iso-certificates-preview-body">
          {!url && !error ? (
            <div className="iso-certificates-preview-state">
              <Loader2 className="animate-spin" size={28} />
              Preparando certificado…
            </div>
          ) : null}

          {error ? (
            <div className="iso-certificates-preview-error">
              <FileText size={22} />
              {error}
            </div>
          ) : null}

          {url ? (
            <iframe
              className="iso-certificates-preview-frame"
              src={url}
              title={`Vista previa de ${slot.label}`}
            />
          ) : null}
        </div>

        <footer className="iso-certificates-modal-footer">
          {url ? (
            <button
              type="button"
              className="iso-certificates-secondary-button"
              onClick={() => window.open(url, "_blank", "noopener,noreferrer")}
            >
              <ExternalLink size={16} />
              Abrir en otra pestaña
            </button>
          ) : null}
          <button type="button" className="iso-certificates-primary-button" onClick={onClose}>
            Cerrar
          </button>
        </footer>
      </div>
    </div>
  );
}
